import { isFocusInEditable } from '../../lib/domUtils';

const VELOCITY_WINDOW_MS = 100;
const MOMENTUM_DECAY = 0.95;
const MOMENTUM_MIN_VELOCITY = 0.5;

interface VelocitySample {
  x: number;
  y: number;
  timestamp: number;
}

export interface PanDragCallbacks {
  getState: () => { panX: number; panY: number; zoom: number };
  setState: (panX: number, panY: number, zoom: number) => void;
  stopMomentumExternal: () => void;
}

/**
 * Imperative controller for space+drag / middle-click pan with momentum.
 * Attach/detach manages all event listeners and the momentum RAF loop.
 */
export class PanDragController {
  private container: HTMLElement;
  private callbacks: PanDragCallbacks;

  private isPanning = false;
  private isSpaceHeld = false;
  private lastX = 0;
  private lastY = 0;
  private velocitySamples: VelocitySample[] = [];
  private momentumFrameId: number | null = null;

  constructor(container: HTMLElement, callbacks: PanDragCallbacks) {
    this.container = container;
    this.callbacks = callbacks;

    // Bind methods for stable listener references
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
  }

  attach(): void {
    window.addEventListener('keydown', this.handleKeyDown, { capture: true });
    window.addEventListener('keyup', this.handleKeyUp, { capture: true });
    this.container.addEventListener('pointerdown', this.handlePointerDown, { capture: true });
    document.addEventListener('pointermove', this.handlePointerMove);
    document.addEventListener('pointerup', this.handlePointerUp);
  }

  detach(): void {
    window.removeEventListener('keydown', this.handleKeyDown, { capture: true });
    window.removeEventListener('keyup', this.handleKeyUp, { capture: true });
    this.container.removeEventListener('pointerdown', this.handlePointerDown, { capture: true });
    document.removeEventListener('pointermove', this.handlePointerMove);
    document.removeEventListener('pointerup', this.handlePointerUp);
    this.stopMomentum();
    document.body.style.cursor = '';
  }

  private stopMomentum(): void {
    if (this.momentumFrameId !== null) {
      cancelAnimationFrame(this.momentumFrameId);
      this.momentumFrameId = null;
    }
  }

  private startMomentum(): void {
    const now = performance.now();
    const cutoff = now - VELOCITY_WINDOW_MS;
    const recent = this.velocitySamples.filter(s => s.timestamp >= cutoff);
    if (recent.length < 2) return;

    const first = recent[0];
    const last = recent[recent.length - 1];
    const dt = (last.timestamp - first.timestamp) / 1000;
    if (dt < 0.001) return;

    let vx = (last.x - first.x) / dt;
    let vy = (last.y - first.y) / dt;
    let lastTime = performance.now();

    const tick = () => {
      const now = performance.now();
      const dtFrame = (now - lastTime) / 1000;
      lastTime = now;

      const decay = Math.pow(MOMENTUM_DECAY, dtFrame * 60);
      vx *= decay;
      vy *= decay;

      if (Math.abs(vx) < MOMENTUM_MIN_VELOCITY && Math.abs(vy) < MOMENTUM_MIN_VELOCITY) {
        this.momentumFrameId = null;
        return;
      }

      const s = this.callbacks.getState();
      this.callbacks.setState(s.panX + vx * dtFrame, s.panY + vy * dtFrame, s.zoom);
      this.momentumFrameId = requestAnimationFrame(tick);
    };

    this.momentumFrameId = requestAnimationFrame(tick);
  }

  private startPan(e: PointerEvent): void {
    this.isPanning = true;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.velocitySamples.length = 0;
    this.velocitySamples.push({ x: e.clientX, y: e.clientY, timestamp: performance.now() });
    this.stopMomentum();
    this.callbacks.stopMomentumExternal();
    document.body.style.cursor = 'grabbing';
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.code !== 'Space' || e.repeat) return;
    if (isFocusInEditable()) return;
    e.preventDefault();
    this.isSpaceHeld = true;
    if (!this.isPanning) document.body.style.cursor = 'grab';
  }

  private handleKeyUp(e: KeyboardEvent): void {
    if (e.code !== 'Space') return;
    this.isSpaceHeld = false;
    if (this.isPanning) {
      this.isPanning = false;
      this.startMomentum();
    }
    document.body.style.cursor = '';
  }

  private handlePointerDown(e: PointerEvent): void {
    if (this.isSpaceHeld && e.button === 0) {
      e.preventDefault();
      e.stopPropagation();
      this.startPan(e);
    } else if (e.button === 1) {
      e.preventDefault();
      this.startPan(e);
    }
  }

  private handlePointerMove(e: PointerEvent): void {
    if (!this.isPanning) return;

    const dx = e.clientX - this.lastX;
    const dy = e.clientY - this.lastY;
    this.lastX = e.clientX;
    this.lastY = e.clientY;

    const s = this.callbacks.getState();
    this.callbacks.setState(s.panX + dx, s.panY + dy, s.zoom);

    const now = performance.now();
    this.velocitySamples.push({ x: e.clientX, y: e.clientY, timestamp: now });
    while (this.velocitySamples.length > 8) this.velocitySamples.shift();
  }

  private handlePointerUp(e: PointerEvent): void {
    if (!this.isPanning) return;
    if (e.button !== 0 && e.button !== 1) return;

    this.isPanning = false;
    this.startMomentum();
    document.body.style.cursor = this.isSpaceHeld ? 'grab' : '';
  }
}
